import { ActionTree } from 'vuex';
import moment from 'moment';
import { EFeatureFlag } from '../enums/feature-flag';
import { FeatureFlagService } from '../services/feature-flag.service';
import { BetaFlagHelper } from '../helpers/beta-flag.helper';
import { IBetaFlagState } from '@/beta-flag/store/beta-flag.types';
import { EBetaFlagAction, EBetaFlagMutation } from '@/beta-flag/store/beta-flag.constants';
import { RootState } from '@/store/store';
import { BetaFlagService } from '@/services/beta-flag.service';
import { translate } from '@/helpers/translation.helper';

export const actions: ActionTree<IBetaFlagState, RootState> = {
  async [EBetaFlagAction.FetchBetaFlags]({ state, commit }) {
    const expires: moment.Moment = moment(state.dismissedBetaExpires);
    try {
      const v3: number = await BetaFlagService.fetchBetaFlag('v3');
      const contentLinkBlock: number = await BetaFlagService.fetchBetaFlag('contentlinkblock');
      const videoBlock: number = await BetaFlagService.fetchBetaFlag('videoblock');
      const editorBlock: number = await BetaFlagService.fetchBetaFlag('editorblock');
      const contentLibrary: number = await FeatureFlagService.evaluateFlag(
        EFeatureFlag.ContentLibrary
      );
      const dealscore: number = await BetaFlagService.fetchBetaFlag('dealscore');
      const editAfterSent: number = await FeatureFlagService.evaluateFlag(
        EFeatureFlag.EditAfterSent
      );

      if (moment().isAfter(expires)) {
        commit(EBetaFlagMutation.SetDismissedBetaExpires, null);
      }
      const flags: {
        v3: number;
        contentLinkBlock: number;
        videoBlock: number;
        editorBlock: number;
        contentLibrary: number;
        dealscore: number;
        editAfterSent: number;
      } = {
        v3,
        contentLinkBlock,
        videoBlock,
        editorBlock,
        contentLibrary,
        dealscore,
        editAfterSent,
      };
      commit(EBetaFlagMutation.SetFlags, flags);
    } catch (e) {
      console.error(e);
    }
  },
  async [EBetaFlagAction.FetchContentLibraryFlag]({ commit }) {
    try {
      const contentLibrary: number = await FeatureFlagService.evaluateFlag(
        EFeatureFlag.ContentLibrary
      );
      commit(EBetaFlagMutation.SetContentLibraryFlag, contentLibrary);
    } catch (e) {
      console.error(e);
    }
  },
  async [EBetaFlagAction.FetchDealscoreFlag]({ commit }) {
    try {
      const dealscore: number = await BetaFlagService.fetchBetaFlag('dealscore');
      commit(EBetaFlagMutation.SetDealscoreFlag, dealscore);
    } catch (e) {
      console.error(e);
    }
  },
  async [EBetaFlagAction.HandleV3OptIn]({ commit, dispatch }, value: '1' | '0') {
    const dismissDays = 21;
    const dismissedBetaExpires: string = moment().add(dismissDays, 'days').toString();
    try {
      await BetaFlagService.setBetaFlag('v3', value);
      commit(EBetaFlagMutation.SetDismissedBetaExpires, dismissedBetaExpires);
      dispatch(EBetaFlagAction.FetchBetaFlags);
    } catch (e) {
      console.error(e);
      dispatch(
        'alert/showDangerAlert',
        translate('could-not-save-the-setting-try-refreshing-the-page'),
        {
          root: true,
        }
      );
    }
  },
  async [EBetaFlagAction.HandleBetaOptIn]({ dispatch }, feature: EFeatureFlag) {
    try {
      await FeatureFlagService.allowFeature(feature);
      dispatch(EBetaFlagAction.HandleDismissOptIn, feature);
      dispatch(EBetaFlagAction.FetchBetaFlags);
    } catch {
      dispatch(
        'alert/showDangerAlert',
        translate('could-not-save-the-setting-try-refreshing-the-page'),
        {
          root: true,
        }
      );
    }
  },
  async [EBetaFlagAction.HandleDismissOptIn]({ rootState }, feature: EFeatureFlag) {
    if (feature === EFeatureFlag.ContentLibrary) {
      const { session } = rootState['session'];
      BetaFlagHelper.dismissContentLibraryOptIn(
        session.entity_hash,
        session.user_hash,
        session.user_role
      );
    }
  },
};
